import axiosInstance from '@/lib/enhancedAxiosInstance';
import { paths } from '@/lib/apiPaths';
import { toast } from 'sonner';
import { logError } from '@/utils/errorHandler';

/**
 * Interface for Channel objects returned from the API
 */
export interface Channel {
  id: number;
  code: string;
  name: string;
  label?: string; // Legacy field for compatibility
  is_active: boolean;
  description?: string | null;
}

/**
 * Helper function to normalize channel data
 * @param raw The raw channel data
 * @returns Normalized channel data
 */
const normalizeChannel = (raw: Channel | null | undefined): Channel => {
  if (!raw) {
    return {
      id: 0,
      code: '',
      name: '',
      is_active: false,
      description: '',
    };
  }

  // Ensure the description is always set
  return {
    ...raw,
    code: raw.code || raw.name,
    description: raw.description || raw.name || raw.code || 'Unknown Channel',
  };
};

/**
 * Service for managing sales channels
 */
const channelService = {
  /**
   * Get all channels for the current organization
   * @returns Promise resolving to an array of Channel objects
   */
  getChannels: async (): Promise<Channel[]> => {
    try {
      const channelsUrl = paths.channels.root();
      const response = await axiosInstance.get(channelsUrl);
      const data = response.data;
      let channels: Channel[] = [];
      if (Array.isArray(data)) {
        channels = data.map(channel => ({
          ...channel,
          code: channel.code || channel.name,
          label: channel.label || channel.name,
        }));
      } else if (data && typeof data === 'object') {
        const results = data.results || data.items || [];
        channels = Array.isArray(results)
          ? results.map(channel => ({
              ...channel,
              code: channel.code || channel.name,
              label: channel.label || channel.name,
            }))
          : [];
      }
      return channels;
    } catch (error) {
      logError(
        'Failed to fetch channels',
        {
          component: 'channelService',
          action: 'getChannels',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
      // Note: Channel load error should be handled by the component using toast translations
      return [];
    }
  },

  /**
   * Create a new channel for the current organization
   * @param code The channel code (e.g., 'ecommerce', 'retail')
   * @param name The human-readable name (e.g., 'E-commerce', 'Retail')
   * @param toastLabels Toast messages from translations hook
   * @returns Promise resolving to the created Channel object
   */
  createChannel: async (code: string, name: string, toastLabels?: any): Promise<Channel> => {
    try {
      const { data } = await axiosInstance.post(paths.channels.root(), {
        code,
        name,
        is_active: true,
      });
      const successMessage = toastLabels?.channels?.createSuccess || 'Channel created successfully';
      toast.success(successMessage);
      return data;
    } catch (error) {
      logError(
        'Failed to create channel',
        {
          component: 'channelService',
          action: 'createChannel',
          metadata: { code, name, error },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = toastLabels?.channels?.createError || 'Failed to create channel';
      toast.error(errorMessage);
      throw error;
    }
  },

  /**
   * Update an existing channel
   * @param id The ID of the channel to update
   * @param changes Object containing the fields to update (code, name, or is_active)
   * @param toastLabels Toast messages from translations hook
   * @returns Promise resolving to the updated Channel object
   */
  updateChannel: async (
    id: number,
    changes: Partial<{ code: string; name: string; is_active: boolean }>,
    toastLabels?: any
  ): Promise<Channel> => {
    try {
      const { data } = await axiosInstance.patch(paths.channels.byId(id), changes);
      const successMessage = toastLabels?.channels?.updateSuccess || 'Channel updated successfully';
      toast.success(successMessage);
      return data;
    } catch (error) {
      logError(
        'Failed to update channel',
        {
          component: 'channelService',
          action: 'updateChannel',
          metadata: { id, changes, error },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = toastLabels?.channels?.updateError || 'Failed to update channel';
      toast.error(errorMessage);
      throw error;
    }
  },

  /**
   * Delete a channel
   * @param id The ID of the channel to delete
   * @param toastLabels Toast messages from translations hook
   * @returns Promise that resolves when the channel is deleted
   */
  deleteChannel: async (id: number, toastLabels?: any): Promise<void> => {
    try {
      await axiosInstance.delete(paths.channels.byId(id));
      const successMessage = toastLabels?.channels?.deleteSuccess || 'Channel deleted successfully';
      toast.success(successMessage);
    } catch (error) {
      logError(
        'Failed to delete channel',
        {
          component: 'channelService',
          action: 'deleteChannel',
          metadata: { id, error },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = toastLabels?.channels?.deleteError || 'Failed to delete channel';
      toast.error(errorMessage);
      throw error;
    }
  },

  /**
   * Get a specific channel by code
   * @param code The channel code
   * @returns Promise with the channel or null if not found
   */
  getChannel: async (code: string): Promise<Channel | null> => {
    try {
      const channels = await channelService.getChannels();
      const channel = channels.find(c => c.code === code);
      return channel ? normalizeChannel(channel) : null;
    } catch (error) {
      logError(
        'Failed to fetch channel by code',
        {
          component: 'channelService',
          action: 'getChannel',
          metadata: { code, error },
        },
        { showToast: false, userFacing: false }
      );
      return null;
    }
  },
};

export default channelService;
